import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import customFetchBase from '@/app/common/utils/customFetchBase';
import { getBaseApiPath } from '@/app/common/api/config';
import { removeCookie } from '@/app/common/utils/cookieService';
import { clearPersistedSelectedDomain } from '@/app/common/utils/clearPersistedSelectedDomain';
import type {
  SignOutResponse,
  UserBudgetResponse,
  UserInfoResponse
} from '@/app/common/api/types/authApiTypes';

const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: (...args) => customFetchBase(getBaseApiPath(), ...args),
  tagTypes: ['UserInfo'],
  endpoints: build => ({
    getUserBudget: build.query<UserBudgetResponse, void | null>({
      query: () => ({
        url: `/go_handler/v2/owner/budget/get`,
        method: 'POST'
      })
    }),
    signOut: build.mutation<void, void>({
      query: () => ({
        url: `/api/user/signout`,
        method: 'POST'
      }),
      transformResponse: async (response: SignOutResponse) => {
        if (response.success) {
          removeCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, true);
          removeCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, true);
          // دامنه انتخاب‌شده را قبل از ریدایرکت logout از persist پاک کن
          await clearPersistedSelectedDomain();
          window.location.replace(
            `${import.meta.env.VITE_ACCOUNT_SERVER_URL}/logout`
          );
        }
      }
    }),
    getUserInfo: build.query<UserInfoResponse | undefined, void>({
      query: () => ({
        url: `/api/user/info`,
        method: 'GET',
        headers: {
          'App-Name': 'mizfa_tools'
        }
      }),
      providesTags: ['UserInfo'],
      transformResponse: (response: UserInfoResponse) => {
        if (!response || response.code !== 200) {
          removeCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, true);
          removeCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, true);
          // پاک کردن دامنه ذخیره‌شده هنگام نامعتبر شدن سشن
          void clearPersistedSelectedDomain();
          window.location.replace('/login');
          return undefined;
        }
        return response;
      },
      transformErrorResponse: (
        _response: FetchBaseQueryError,
        meta: { response?: Response } | undefined
      ) => {
        if (meta?.response?.status === 500) {
          removeCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, true);
          removeCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, true);
          void clearPersistedSelectedDomain();
          window.location.replace('/login');
        }
      }
    })
  })
});

export const {
  useGetUserInfoQuery,
  useSignOutMutation,
  useLazyGetUserInfoQuery,
  useGetUserBudgetQuery
} = authApi;

export default authApi;
